import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { signaturesApi } from '../api';
import { toast } from 'sonner';
import { extractApiError } from '../utils/dateValidation';
import { PenTool, RotateCcw, CheckCircle2, Loader2, XCircle } from 'lucide-react';

const SignaturePad = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e) => {
    drawingRef.current = true;
    canvasRef.current.setPointerCapture(e.pointerId);
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    hasDrawnRef.current = true;
    onChange(true);
  };

  const stopDraw = () => { drawingRef.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    hasDrawnRef.current = false;
    onChange(false);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        onPointerDown={startDraw}
        onPointerMove={draw}
        onPointerUp={stopDraw}
        onPointerLeave={stopDraw}
        style={{
          width: '100%',
          height: '220px',
          background: '#fff',
          borderRadius: '0.75rem',
          border: '1px solid rgba(37,99,235,0.3)',
          touchAction: 'none',
          cursor: 'crosshair',
          display: 'block',
        }}
      />
      <button type="button" onClick={clear} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mt-2">
        <RotateCcw size={14} /> Limpar
      </button>
    </div>
  );
};

const SignDocument = () => {
  const { module, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [hasSignature, setHasSignature] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const canvasWrapperRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await signaturesApi.get(module, token);
        setInfo(res.data);
      } catch (err) {
        setError(extractApiError(err, 'Link de assinatura inválido ou expirado'));
      } finally {
        setLoading(false);
      }
    })();
  }, [module, token]);

  const handleConfirm = async () => {
    const canvas = canvasWrapperRef.current?.querySelector('canvas');
    if (!hasSignature || !canvas) {
      toast.error('Desenhe sua assinatura antes de confirmar');
      return;
    }
    setSubmitting(true);
    try {
      const signature_image = canvas.toDataURL('image/png');
      await signaturesApi.submit(module, token, { signature_image });
      setSubmitted(true);
      toast.success('Assinatura registrada com sucesso');
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao registrar assinatura'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #060b17 0%, #0b1425 50%, #07111e 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem',
    }}>
      <div className="card p-8 max-w-md w-full space-y-5">
        <div className="flex items-center gap-3">
          <PenTool size={26} className="text-blue-400" />
          <h1 className="text-xl font-bold text-white">Assinatura Digital</h1>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={28} className="animate-spin text-blue-400" />
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <XCircle size={40} className="text-red-400" />
            <p className="text-white font-medium">Não foi possível carregar esta assinatura</p>
            <p className="text-sm text-gray-400">{error}</p>
          </div>
        )}

        {!loading && !error && info && (submitted || info.already_signed) && (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <CheckCircle2 size={40} className="text-green-400" />
            <p className="text-white font-medium">Documento já assinado</p>
            <p className="text-sm text-gray-400">
              {info.participant_name}, sua assinatura em <span className="text-white">{info.document_label}</span> já foi registrada.
            </p>
          </div>
        )}

        {!loading && !error && info && !submitted && !info.already_signed && (
          <>
            <div className="text-sm text-gray-300 space-y-1">
              <p>Documento: <span className="text-white font-medium">{info.document_label}</span></p>
              <p>Participante: <span className="text-white font-medium">{info.participant_name}</span></p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Desenhe sua assinatura abaixo</label>
              <div ref={canvasWrapperRef}>
                <SignaturePad onChange={setHasSignature} />
              </div>
            </div>

            <button
              onClick={handleConfirm}
              disabled={submitting}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {submitting ? <><Loader2 size={18} className="animate-spin" /> Enviando...</> : 'Confirmar Assinatura'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SignDocument;
